export type Operation = 'add' | 'subtract' | 'multiply' | 'divide';

export type Mode = 'result' | 'part';

export type Operator = '+' | '-' | ':' | '·';

export type HiddenElement = 'part1' | 'part2' | 'result';

export interface Range {
  readonly min: number;
  readonly max: number;
}

export interface EntryData {
  readonly part1: number;
  readonly part2: number;
  readonly result: number;
  readonly operator: Operator;
  readonly hiddenElement: HiddenElement;
}

function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min); // The maximum is inclusive and the minimum is inclusive
}

function getHiddenElement(mode: Mode): HiddenElement {
  return mode === 'part'
    ? Math.random() >= 0.5
      ? 'part1'
      : 'part2'
    : 'result';
}

function createAddEntry(range: Range, mode: Mode): EntryData {
  const part1 = getRandomInt(range.min, range.max);
  const part2 = getRandomInt(range.min, range.max);
  const result = part1 + part2;
  const hiddenElement = getHiddenElement(mode);
  const operator = '+';

  return {part1, part2, result, hiddenElement, operator};
}

function createSubtractEntry(range: Range, mode: Mode): EntryData {
  let part1 = getRandomInt(range.min, range.max);
  let part2 = getRandomInt(range.min, range.max);
  if (part1 < part2) {
    const temp = part1;
    part1 = part2;
    part2 = temp;
  }
  const result = part1 - part2;
  const hiddenElement = getHiddenElement(mode);
  const operator = '-';

  return {part1, part2, result, hiddenElement, operator};
}

function createMultiply(
  range: Range,
  mode: Mode,
  multiplyFactor?: number,
): EntryData {
  let part1 = getRandomInt(1, range.max);
  let part2 = getRandomInt(range.min, range.max);
  if (multiplyFactor) {
    const multipyFactorElement = getHiddenElement('part');
    part1 = multipyFactorElement === 'part2' ? multiplyFactor : part1;
    part2 = multipyFactorElement === 'part1' ? multiplyFactor : part2;
  }
  const result = part1 * part2;
  const hiddenElement = getHiddenElement(mode);
  const operator = '·';

  return {part1, part2, result, hiddenElement, operator};
}

function generateRandomParts(
  range: Range,
  divideFactor?: number,
  baseFactorElement?: 'part1' | 'part2' | 'result',
) {
  const maxRange = divideFactor
    ? Math.abs(range.max / divideFactor)
    : Math.abs(range.max / 2);
  const part1 =
    baseFactorElement === 'part2' && divideFactor
      ? divideFactor
      : getRandomInt(1, maxRange);
  const part2 =
    baseFactorElement === 'part1' && divideFactor
      ? divideFactor
      : getRandomInt(2, Math.abs(range.max / part1));
  while (part1 * part2 > range.max) {
    generateRandomParts(range, divideFactor, baseFactorElement);
  }
  return [part1, part2];
}

function generateRandomForDivide(
  range: Range,
  divideFactor?: number,
): [number, number] {
  const baseFactorElement = divideFactor ? getHiddenElement('part') : undefined;
  const [part1, part2] = generateRandomParts(
    range,
    divideFactor,
    baseFactorElement,
  );

  return [part1, part2];
}

function createDivide(
  range: Range,
  mode: Mode,
  divideFactor?: number,
): EntryData {
  const [part1, part2] = generateRandomForDivide(range, divideFactor);
  const result = part1 * part2;
  const hiddenElement = getHiddenElement(mode);
  const operator = ':';

  return {part1: result, part2: part1, result: part2, hiddenElement, operator};
}

export function generateUniqueEntry(
  entries: EntryData[],
  range: Range,
  operation: Operation,
  mode: Mode,
  baseFactor?: number,
): EntryData {
  let entry: EntryData;
  switch (operation) {
    case 'add':
      entry = createAddEntry(range, mode);
      break;
    case 'subtract':
      entry = createSubtractEntry(range, mode);
      break;
    case 'multiply':
      entry = createMultiply(range, mode, baseFactor);
      break;
    case 'divide':
      entry = createDivide(range, mode, baseFactor);
      break;
  }
  if (
    entries.find(
      (item) => item.part1 == entry.part1 && item.part2 == entry.part2,
    )
  ) {
    return generateUniqueEntry(entries, range, operation, mode, baseFactor);
  }

  return entry;
}
